/**
 * 데모 시나리오 — 화면 작업자가 **키 하나만 갈아끼워** 상태를 통째로 바꾸는 자리.
 * 출처: 와이어프레임 `STATES`(959행) 5종을 그대로 재현한 것 + 끊김 1종.
 *
 * 🔴 **와이어프레임의 `S()`(985행)를 옮기지 않았다.** 그 함수는 `phase` 로 `allowed` 배열을
 *    계산해 만드는데, 그건 「버튼 활성은 서버가 준 `allowed_commands` 그대로」 규약 위반이다
 *    (README §11-3 #3). 여기서는 **각 시나리오가 서버 응답을 그대로 들고 있다.** 계산 함수는 없다.
 *
 * 🔴 `DISCONNECTED` 는 와이어프레임에 **없는 시나리오다.** §11-3 #1 이 지적한 구멍
 *    (stale 인데 상태변경 버튼이 살아 있다)을 화면이 막았는지 확인하라고 내가 넣었다.
 */
import type {
  AlarmEvent,
  Batch,
  ChamberRow,
  ControlLockLease,
  ModuleSnapshot,
  SafetyState,
  SystemMode,
  TimelineSegment,
  TrayCell,
} from '@ctcells/contract'

import { ALARMS_ERROR, ALARMS_IDLE, ALARMS_READY, ALARMS_RUNNING } from './alarms.ts'
import { BATCH_ERROR, BATCH_READY, BATCH_RUNNING } from './batches.ts'
import {
  CHAMBER_ROWS_EMPTY,
  CHAMBER_ROWS_ERROR,
  CHAMBER_ROWS_READY,
  CHAMBER_ROWS_RUNNING,
  TRAY_LAYOUT,
  TRAY_LAYOUT_EMPTY,
} from './chambers.ts'
import {
  MODULES_ERROR,
  MODULES_IDLE,
  MODULES_MANUAL,
  MODULES_READY,
  MODULES_RUNNING,
  MODULES_STALE,
} from './modules.ts'

export const DEMO_SCENARIO_KEYS = [
  'RUNNING',
  'IDLE',
  'READY',
  'MANUAL',
  'ERROR',
  'DISCONNECTED',
] as const

export type DemoScenarioKey = (typeof DEMO_SCENARIO_KEYS)[number]

/**
 * 구독 2채널의 상태. `@ctcells/realtime` 의 `RealtimeState` 와 **같은 모양**이지만
 * fixture 가 realtime 패키지(및 react)에 딸려가지 않도록 여기서 따로 선언한다.
 * 실물이 붙으면 이 값은 `useRealtime()` 이 준다.
 */
export interface DemoRealtimeState {
  events: {
    status: 'idle' | 'connecting' | 'open' | 'closed'
    lastMessageAt: number | null
    stale: boolean
  }
  safety: {
    status: 'idle' | 'connecting' | 'open' | 'closed'
    lastMessageAt: number | null
    stale: boolean
  }
  /** 🔴 둘 중 하나라도 stale 이면 제어 불가. **서버·셸이 준 값이다 — 화면이 다시 계산하지 않는다** */
  controlDisabled: boolean
}

const LAST_MESSAGE_AT = Date.parse('2026-09-11T14:20:03+09:00')

const CONNECTED: DemoRealtimeState = {
  events: { status: 'open', lastMessageAt: LAST_MESSAGE_AT, stale: false },
  safety: { status: 'open', lastMessageAt: LAST_MESSAGE_AT, stale: false },
  controlDisabled: false,
}

const DISCONNECTED: DemoRealtimeState = {
  events: { status: 'closed', lastMessageAt: LAST_MESSAGE_AT - 45_000, stale: true },
  safety: { status: 'closed', lastMessageAt: LAST_MESSAGE_AT - 45_000, stale: true },
  controlDisabled: true,
}

/**
 * 계획 3구간. 합 08:52:24.
 * ⚠ 「구간」 개념은 계약에 없다 — 레시피 payload 와 `batch_progress` 를 화면이 묶은 것이다.
 */
const PLAN = { cent: 3060, asm: 2700, stain: 26184 } as const

function segments(progress: [number, number, number], shiftSec = 0): TimelineSegment[] {
  return [
    {
      module_id: 'cent',
      planned_sec: PLAN.cent,
      estimated_sec: PLAN.cent + shiftSec,
      progress: progress[0],
      current: progress[0] > 0 && progress[0] < 1,
    },
    {
      module_id: 'asm',
      planned_sec: PLAN.asm,
      estimated_sec: PLAN.asm,
      progress: progress[1],
      current: progress[1] > 0 && progress[1] < 1,
    },
    {
      module_id: 'stain',
      planned_sec: PLAN.stain,
      estimated_sec: PLAN.stain,
      progress: progress[2],
      current: progress[2] > 0 && progress[2] < 1,
    },
  ]
}

const LOCK_MINE: ControlLockLease = {
  control_token: 'ctl-demo-token',
  holder: 'hyungwoo.cho',
  acquired_at: '2026-09-11T14:18:00+09:00',
  expires_at: '2026-09-11T14:20:30+09:00',
  // v0.9.3 정본 칸(2026-10-07) — 갱신 간격(서버 `CONTROL_LOCK_HEARTBEAT_SEC` 기본 10)
  heartbeat_sec: 10,
}

const LOCK_OTHER: ControlLockLease = {
  ...LOCK_MINE,
  control_token: '',
  holder: 'eric.cho',
}

const SAFETY_NORMAL: SafetyState = { state: 'normal', cleared: true, version: 5 }

const SAFETY_STOPPED: SafetyState = {
  state: 'emergency_stop',
  cleared: false,
  unresolved_conditions: ['CENT_IMBALANCE'],
  requires_ready_gate: true,
  version: 9,
}

const MODE_NORMAL: SystemMode = {
  mode: 'normal',
  since: '2026-09-11T08:00:00+09:00',
  changed_by: 'eric.cho',
  simulation_labeled: false,
  version: 3,
}

/** 수동 조작 = 운영 모드 `maintenance` + 모듈 `control_owner='local'` 의 조합이다 */
const MODE_MAINTENANCE: SystemMode = {
  ...MODE_NORMAL,
  mode: 'maintenance',
  since: '2026-09-11T14:02:00+09:00',
  version: 4,
}

export interface DemoScenario {
  key: DemoScenarioKey
  /** 진행 중 작업. null = 대기 */
  batch: Batch | null
  /** 상시 작업 줄 3구간 */
  segments: TimelineSegment[]
  /** 계획 대비 편차(초). 0 이면 계획대로 */
  deviation_sec: number
  modules: ModuleSnapshot[]
  alarms: AlarmEvent[]
  safety: SafetyState
  systemMode: SystemMode
  /** 제어락. null = 아무도 안 쥐고 있다 */
  controlLock: ControlLockLease | null
  /** 내가 쥔 락인가. `holder` 비교를 화면이 하지 않도록 데이터로 준다 */
  controlLockIsMine: boolean
  chambers: ChamberRow[]
  tray: TrayCell[]
  realtime: DemoRealtimeState
}

export const SCENARIO_RUNNING: DemoScenario = {
  key: 'RUNNING',
  batch: BATCH_RUNNING,
  segments: segments([0.24, 0, 0]),
  // 6축 재시도 ×4 로 +04:10
  deviation_sec: 250,
  modules: MODULES_RUNNING,
  alarms: ALARMS_RUNNING,
  safety: SAFETY_NORMAL,
  systemMode: MODE_NORMAL,
  controlLock: LOCK_MINE,
  controlLockIsMine: true,
  chambers: CHAMBER_ROWS_RUNNING,
  tray: TRAY_LAYOUT,
  realtime: CONNECTED,
}

export const SCENARIO_IDLE: DemoScenario = {
  key: 'IDLE',
  batch: null,
  // 🔴 IDLE 에도 구간 골격은 남는다 — 조건부 렌더로 행을 없애지 않는다(높이 고정)
  segments: segments([0, 0, 0]),
  deviation_sec: 0,
  modules: MODULES_IDLE,
  alarms: ALARMS_IDLE,
  safety: SAFETY_NORMAL,
  systemMode: MODE_NORMAL,
  controlLock: null,
  controlLockIsMine: false,
  chambers: CHAMBER_ROWS_EMPTY,
  tray: TRAY_LAYOUT_EMPTY,
  realtime: CONNECTED,
}

export const SCENARIO_READY: DemoScenario = {
  key: 'READY',
  batch: BATCH_READY,
  segments: segments([0, 0, 0]),
  deviation_sec: 0,
  modules: MODULES_READY,
  alarms: ALARMS_READY,
  safety: SAFETY_NORMAL,
  systemMode: MODE_NORMAL,
  controlLock: LOCK_MINE,
  controlLockIsMine: true,
  chambers: CHAMBER_ROWS_READY,
  tray: TRAY_LAYOUT,
  realtime: CONNECTED,
}

/**
 * 수동 조작. 🔴 **작업 상태가 아니다** — 운영 모드 `maintenance` ∧ 모듈 단독 제어다.
 * 시작·중지·복구는 전부 비활성이고 E-STOP 만 살아 있다. 그 사실이 데이터로 보인다:
 * `batch: null` + 모든 모듈 `allowed_commands: []` + `control_owner: 'local'`.
 */
export const SCENARIO_MANUAL: DemoScenario = {
  key: 'MANUAL',
  batch: null,
  segments: segments([0, 0, 0]),
  deviation_sec: 0,
  modules: MODULES_MANUAL,
  alarms: ALARMS_IDLE,
  safety: SAFETY_NORMAL,
  systemMode: MODE_MAINTENANCE,
  controlLock: LOCK_MINE,
  controlLockIsMine: true,
  chambers: CHAMBER_ROWS_EMPTY,
  tray: TRAY_LAYOUT_EMPTY,
  realtime: CONNECTED,
}

export const SCENARIO_ERROR: DemoScenario = {
  key: 'ERROR',
  batch: BATCH_ERROR,
  segments: segments([0.06, 0, 0]),
  deviation_sec: 0,
  modules: MODULES_ERROR,
  alarms: ALARMS_ERROR,
  safety: SAFETY_STOPPED,
  systemMode: MODE_NORMAL,
  // 타인이 락을 쥐고 있다 — admin 만 강제 해제할 수 있다
  controlLock: LOCK_OTHER,
  controlLockIsMine: false,
  chambers: CHAMBER_ROWS_ERROR,
  tray: TRAY_LAYOUT,
  realtime: CONNECTED,
}

/**
 * 🔴 구독 끊김. 마지막으로 받은 값은 실행 중이었지만 **그것을 현재로 보이면 안 된다.**
 *    모든 모듈 `stale: true` · `allowed_commands: []` · 작업은 `null`(집합을 믿을 수 없다) ·
 *    `realtime.controlDisabled: true`.
 *    ⚠ E-STOP 은 여기에 걸리지 않는다 — 역할·제어락·상태 무관 항상 활성이다.
 */
export const SCENARIO_DISCONNECTED: DemoScenario = {
  key: 'DISCONNECTED',
  batch: null,
  segments: segments([0, 0, 0]),
  deviation_sec: 0,
  modules: MODULES_STALE,
  alarms: ALARMS_RUNNING,
  safety: SAFETY_NORMAL,
  systemMode: MODE_NORMAL,
  controlLock: null,
  controlLockIsMine: false,
  chambers: CHAMBER_ROWS_EMPTY,
  tray: TRAY_LAYOUT_EMPTY,
  realtime: DISCONNECTED,
}

export const DEMO_SCENARIOS: Record<DemoScenarioKey, DemoScenario> = {
  RUNNING: SCENARIO_RUNNING,
  IDLE: SCENARIO_IDLE,
  READY: SCENARIO_READY,
  MANUAL: SCENARIO_MANUAL,
  ERROR: SCENARIO_ERROR,
  DISCONNECTED: SCENARIO_DISCONNECTED,
}

/** 기본 시나리오. 화면 작업자가 아무것도 안 고르면 이것 */
export const DEFAULT_SCENARIO: DemoScenario = SCENARIO_RUNNING
